import { HttpErrorResponse } from '@angular/common/http';
import { ExceptionHandler } from './exceptions-handler';

/**
 * Centralized API error messages - single source of truth.
 * Do not duplicate these strings elsewhere in the app.
 */
/** Set this header to 'true' to suppress global error toast (e.g. for background polling) */
export const SUPPRESS_ERROR_TOAST_HEADER = 'X-Suppress-Error-Toast';

/**
 * Set this header to 'true' to skip attaching Authorization/AppType/Role/Company headers.
 * For public, unauthenticated endpoints (vendor invite link verify/onboard, external payment
 * details) opened straight from an emailed link — a stale JWT left over from another logged-in
 * session on the same browser must not be sent along with these anonymous requests.
 */
export const SKIP_AUTH_HEADER = 'X-Skip-Auth';

/**
 * Set this header to 'true' to send the request WITHOUT the Company header. For platform-wide
 * data (Platform Adoption) that must not be narrowed by the header's organization switcher.
 * The interceptor consumes it: it is never sent to the server.
 */
export const OMIT_COMPANY_HEADER = 'X-Omit-Company';

/** Parses API error body when responseType is text (body arrives as JSON string). */
export function parseApiErrorBody(
  error: unknown
): Record<string, unknown> | null {
  const body = (error as { error?: unknown })?.error;

  if (body && typeof body === 'object') {
    return body as Record<string, unknown>;
  }

  if (typeof body === 'string' && body.trim()) {
    try {
      const parsed = JSON.parse(body);
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  return null;
}

export function isInviteVerifyRequestUrl(url: string): boolean {
  return url.includes('/verify/url/');
}

export const ApiErrorMessages = {
  generic: 'Something went wrong. Please try again later.',
  network: 'Network error. Please check your connection and try again.',
  server: 'Server error. Please try again later.',
  unauthorized: 'Authentication Error',
  notFound: 'The requested resource was not found.',
  forbidden: 'You do not have permission to perform this action.',
  sessionExpired: 'Session Expired',
  noRefreshToken: 'No refresh token found',
  invalidUser: 'Invalid User',
  roleRemoved: 'This role has been removed from this user',
  previewNotSupported: 'Preview not supported for this file type.',
  storeAlreadyInGroup: 'This store already exists in another store group.',
  fileNotFoundForPreview: 'File not found for preview.',
  invalidFileDetails: 'Invalid file details.',
  fileUrlMissing: 'File URL is missing.',
  fileStreamNotFound: 'File stream not found.',
  failedToLoadFilePreview: 'Failed to load file for preview.',
} as const;

/**
 * Extracts user-friendly error message from HTTP error.
 * Priority: Known exceptions > API message > Status-based default > Generic fallback.
 */
export function getApiErrorMessage(error: any, defaultMessage: string = ApiErrorMessages.generic): string {
  const knownMessage = ExceptionHandler.getExceptionMessage(error);
  if (knownMessage) {
    return knownMessage;
  }

  const apiMessage = error?.error?.message || error?.message;
  if (apiMessage && typeof apiMessage === 'string') {
    return apiMessage;
  }

  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 0:
        return ApiErrorMessages.network;
      case 401:
        return ApiErrorMessages.unauthorized;
      case 403:
        return ApiErrorMessages.forbidden;
      case 404:
        return ApiErrorMessages.notFound;
      case 500:
      case 502:
      case 503:
        return ApiErrorMessages.server;
    }
  }

  return defaultMessage;
}

/** BE `OrganizationInactiveException` (403): the user's organization has been deactivated by a System Admin. */
export const ORGANIZATION_INACTIVE_EXCEPTION = 'OrganizationInactiveException';

export function isOrganizationInactiveError(error: any): boolean {
  let body = error?.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      body = null;
    }
  }
  return body?.title === ORGANIZATION_INACTIVE_EXCEPTION;
}
